import React, { useEffect, useState } from "react";
import styled from "styled-components";
import axios from "axios";
import { FaDownload } from "react-icons/fa";
import { format } from "date-fns";
import * as pdfMake from "pdfmake/build/pdfmake";
import * as pdfFonts from "pdfmake/build/vfs_fonts";
import AdminNavbar from "./AdminNavbar";

pdfMake.vfs = pdfFonts.default;

const Container = styled.div`
  min-height: 100vh;
  background: #f2f2f2;
  font-family: "Inter", sans-serif;
  display: flex;
  flex-direction: column;
`;

const Content = styled.div`
  padding: 20px;
`;

const FilterBar = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 15px;

  input {
    padding: 8px;
    font-size: 14px;
    border-radius: 4px;
    border: 1px solid #ccc;
    flex: 1;
  }
`;

const Table = styled.table`
  width: 100%;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  border-collapse: collapse;

  th, td {
    padding: 12px 16px;
    border-bottom: 1px solid #eee;
    text-align: left;
  }

  th {
    background: #f9f9f9;
    font-weight: bold;
  }
`;

const DownloadButton = styled.button`
  background: #4caf50;
  color: white;
  padding: 6px 10px;
  border: none;
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    background: #388e3c;
  }
`;

const InvoiceNumber = styled.span`
  font-weight: bold;
  color: #2c7be5;
  font-size: 18px;
`;

const PriceText = styled.span`
  font-weight: 600;
  color:rgb(14, 14, 14);
`;

const TotalWrapper = styled.div`
  background: #d1fae5;
  color: #047857;
  padding: 20px;
  margin-bottom: 20px;
  text-align: center;
  font-size: 24px;
  font-weight: bold;
  border-radius: 12px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
`;

const TotalAmount = styled.span`
  font-size: 20px;
  color: #4caf50;
`;


const AdminInvoicesList = () => {
  const [invoices, setInvoices] = useState([]);
  const [filters, setFilters] = useState({
    email: "",
    license: "",
    invoiceId: "",
    dateFrom: "",
    dateTo: "",
  });
  

  useEffect(() => {
    axios
      .get("https://server229256.nazwa.pl/api/generated-invoices")
      .then((res) => setInvoices(res.data))
      .catch((err) => console.error("❌ Error fetching invoices:", err));
  }, []);

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const filteredInvoices = invoices.filter((inv) => {
    const invoiceDate = new Date(inv.generatedAt);
    invoiceDate.setHours(0, 0, 0, 0); // normalize to 00:00:00
    
    const from = filters.dateFrom ? new Date(filters.dateFrom) : null;
    const to = filters.dateTo ? new Date(filters.dateTo) : null;
  
    if (from) from.setHours(0, 0, 0, 0);
    if (to) to.setHours(23, 59, 59, 999); // include the whole 'to' day
  
    return (
      inv.email.toLowerCase().includes(filters.email.toLowerCase()) &&
      inv.license.toLowerCase().includes(filters.license.toLowerCase()) &&
      inv.invoiceNumber?.toUpperCase().includes(filters.invoiceId.toUpperCase()) &&
      (!from || invoiceDate >= from) &&
      (!to || invoiceDate <= to)
    );
  });
  
  

  const downloadPdf = async (id) => {
    try {
      const { data: invoice } = await axios.get(`https://server229256.nazwa.pl/api/invoice-pdf/${id}`);
      const invoiceNumber = invoice.invoiceNumber;
      const formattedDate = format(new Date(invoice.generatedAt), "yyyy-MM-dd");
      const formattedPurchase = format(new Date(invoice.creationTime), "yyyy-MM-dd");

      const brutto = parseFloat(invoice.price);
      const netto = +(brutto / 1.23).toFixed(2);
      const vat = +(brutto - netto).toFixed(2);


      const docDefinition = {
        defaultStyle: { font: "Roboto", fontSize: 10 },
        content: [
          { text: `Faktura VAT - ${invoiceNumber}`, style: "header" },
          { text: `Data wystawienia: ${formattedDate}` },
          { text: `Data zakupu: ${formattedPurchase}`, margin: [0, 0, 0, 10] },
          {
            columns: [
              {
                width: "50%",
                text: [
                  { text: "Wystawca:\n", bold: true },
                  `${invoice.emitter.nazwa}\n`,
                  `NIP: ${invoice.emitter.nip}\n`,
                  `REGON: ${invoice.emitter.regon}\n`,
                  `Adres: ${invoice.emitter.adres}\n`,
                ],
              },
              {
                width: "50%",
                text: [
                  { text: "Odbiorca:\n", bold: true },
                  `${invoice.klient.nazwa}\n`,
                  `NIP: ${invoice.klient.nip}\n`,
                  `Adres: ${invoice.klient.ulica} ${invoice.klient.nrNieruchomosci}\n`,
                  `${invoice.klient.kodPocztowy} ${invoice.klient.miejscowosc}\n`,
                  `${invoice.klient.gmina}, ${invoice.klient.powiat}, ${invoice.klient.wojewodztwo}\n`,
                ],
              },
            ],
            margin: [0, 0, 0, 10],
          },
          {
            table: {
              headerRows: 1,
              widths: ["*", "auto", "auto", "auto"],
              body: [
                [
                  { text: "Produkt", bold: true, fillColor: "#f2f2f2" },
                  { text: "Cena Netto", bold: true, fillColor: "#f2f2f2" },
                  { text: "VAT (23%)", bold: true, fillColor: "#f2f2f2" },
                  { text: "Cena Brutto", bold: true, fillColor: "#f2f2f2" }
                ],
                [
                  invoice.license,
                  `${netto.toFixed(2)} zł`,
                  `${vat.toFixed(2)} zł`,
                  `${brutto.toFixed(2)} zł`
                ]
              ]
            },
            layout: "lightHorizontalLines",
            margin: [0, 0, 0, 20]
          },
          {
            text: "Faktura została opłacona.",
            italics: true,
            margin: [0, 0, 0, 20],
          },
          {
            columns: [
              { text: '', width: '*' },
              {
                stack: [
                  { text: '...................................................', alignment: 'right' },
                  { text: 'Podpis', alignment: 'right' }
                ],
                width: 'auto'
              }
            ]
          }
        ],
        styles: {
          header: { fontSize: 16, bold: true, marginBottom: 10 }
        },
        footer: (currentPage, pageCount) => ({
          columns: [{ text: `Strona ${currentPage} z ${pageCount}`, alignment: "center", margin: [0, 10, 0, 0] }],
        }),
      };

      pdfMake.createPdf(docDefinition).download(`${invoiceNumber}.pdf`);

    } catch (err) {
      console.error("❌ PDF download error:", err.message);
      alert("Błąd podczas pobierania faktury.");
    }
  };


  
  const licenseColorMap = {
    "STANDARD - 1 miesiąc": "#2196F3",
    "STANDARD - 6 miesięcy": "#1976D2",
    "STANDARD - 1 rok": "#0D47A1",
    "PRO - 1 miesiąc": "#4CAF50",
    "PRO - 6 miesięcy": "#388E3C",
    "PRO - 1 rok": "#1B5E20",
    "ENTERPRISE - 1 miesiąc": "#9C27B0",
    "ENTERPRISE - 6 miesięcy": "#7B1FA2",
    "ENTERPRISE - 1 rok": "#4A148C",
    "DEMO BASIC - 7 dni": "#FFC107",
    "DEMO EXTENDED - 15 dni": "#FF9800",
    "NO EXPIRE": "#FF5722",
  };
  

  return (
    <Container>
      <AdminNavbar />
      <Content>
        <h2>Faktury</h2>

        <FilterBar>
  <input
    name="invoiceId"
    placeholder="Filtruj po numerze faktury"
    value={filters.invoiceId}
    onChange={handleFilterChange}
  />
  <input
    name="email"
    placeholder="Filtruj po e-mailu"
    value={filters.email}
    onChange={handleFilterChange}
  />
  <input
    name="license"
    placeholder="Filtruj po licencji"
    value={filters.license}
    onChange={handleFilterChange}
  />
  <input
    type="date"
    name="dateFrom"
    value={filters.dateFrom}
    onChange={handleFilterChange}
  />
  <input
    type="date"
    name="dateTo"
    value={filters.dateTo}
    onChange={handleFilterChange}
  />
</FilterBar>
<TotalWrapper>
  <span>Łączna wartość faktur: </span>
  <TotalAmount>
    {filteredInvoices.reduce((sum, inv) => sum + parseFloat(inv.price || 0), 0).toFixed(2)} zł
  </TotalAmount>
</TotalWrapper>


        <Table>
          <thead>
            <tr>
              <th>Numer</th>
              <th>Email</th>
              <th>Licencja</th>
              <th>Cena (Brutto)</th>
              <th>Data</th>
              <th>Akcja</th>
            </tr>
          </thead>
          <tbody>
            {filteredInvoices.map((inv) => (
           <tr key={inv._id}>
           <td><InvoiceNumber>{inv.invoiceNumber}</InvoiceNumber></td>
           <td>{inv.email}</td>
           <td>
  {inv.license ? (
    <span
      style={{
        backgroundColor: licenseColorMap[inv.license] || "#000",
        color: "white",
        padding: "5px 10px",
        borderRadius: "6px",
        fontWeight: "bold",
        display: "inline-block",
        minWidth: "140px",
        textAlign: "center",
      }}
    >
      {inv.license}
    </span>
  ) : (
    "—"
  )}
</td>

           <td><PriceText>{inv.price} zł</PriceText></td>
           <td>{format(new Date(inv.generatedAt), "yyyy-MM-dd")}</td>
           <td>
             <DownloadButton onClick={() => downloadPdf(inv._id)}>
               <FaDownload /> Pobierz
             </DownloadButton>
           </td>
         </tr>
         
            ))}
          </tbody>
        </Table>
      </Content>
    </Container>
  );
};

export default AdminInvoicesList;
